import { watch, watchEffect } from 'vue';
import { assert } from '@/util/assert';
import type { LogFunction } from '@/util/logger';
import { stopAll, type StopHandle } from '@/util/stopHandle';
import { useDebounceFn, watchImmediate } from '@vueuse/core';
import type { SideId } from '@/domain/anatomy/side';
import type { AcetabularAngles } from '@/domain/anatomy/pelvis/acetabularAngles';
import { Matrix4, type Vector3 } from 'three';
import { cross } from '@/domain/geometry/vector3';
import { isRigidMatrix, positionalPart } from '@/domain/geometry/matrix';
import {
    formatFloat,
    formatMatrixBasis,
    formatRadians,
    formatRadiansAsDegrees,
    formatVector,
    indent,
    joinIndented,
} from '@/domain/geometry/formatMath';
import type { AnatomicalOffset } from '@/domain/anatomy/pelvis/anatomicalOffset';
import { updateCupCoverage } from '@/views/3d-planner/cupCoverage/updateCupCoverage';
import type { PlannerStore } from '@/views/3d-planner/store/plannerStore';
import type { PlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel.types';
import { logValidation } from '@/views/3d-planner/operations/logValidation';
import { cupWorldOffsetFromApi, type FittedCup } from '@/views/3d-planner/fitting/fittedCup';
import { planeTransform } from '@/lib/threeScene/nodes/plane';
import { useDeveloperSettings } from '@/stores/developerSettings';
import { computeCupCollisionSurfaceUrl, computeLinerUrl } from '@/views/3d-planner/fitting/componentUrls';
import { updateCrossSection } from '@/views/3d-planner/scene/crossSection';

/** Updates to the scene that relate to the cup and liner. Reads domain off the Model, scene off the VM. */
export function updateCup(model: PlannerStore, scene: PlannerSceneViewModel): StopHandle {
    const settings = useDeveloperSettings();
    return stopAll(
        watchImmediate(
            () => scene.plannerMode !== 'stem',
            (show) => (scene.nodes.cupGroup.visible = show),
        ),
        watchEffect(() => {
            scene.nodes.cup.geometrySource = model.template?.cupUrl ?? null;
        }),
        watchEffect(() => {
            scene.nodes.liner.geometrySource =
                model.template && model.catalog ? computeLinerUrl(model) : null;
        }),
        watchEffect(() => {
            scene.nodes.cupCollisionSurface.geometrySource = computeCupCollisionSurfaceUrl(model);
        }),
        watchImmediate(
            () => model.fittedCup !== null && settings.show3dFeatures === true,
            (show) => {
                [scene.nodes.cupNormalAxis, scene.nodes.cupApAxis, scene.nodes.cupSiAxis].forEach(
                    (node) => (node.visible = show),
                );
            },
        ),
        watchEffect(() => _updateCupComponents(model, scene)),
        watchEffect(() => _updateCupGroupTransform(model, scene)),
        _logCupGroupTransforms(model, scene),
        updateCupCoverage(model, scene.nodes),
        updateCrossSection(scene, scene.nodes.cupCoronalCrossSection),
    );
}

function _updateCupComponents(model: PlannerStore, scene: PlannerSceneViewModel): void {
    if (!model.fittedCup) {
        return;
    }
    const cupTransform = model.fittedCup.cupTransform;
    const linerTransform = model.fittedCup.linerTransform;
    const fittedHJC = model.fittedCup.fittedHjc;

    // We use the fitted cup-group transform here in order to calculate the transform of cup liner
    // features relative to the cup-group.
    const cupGroupTransform = cupTransform.clone().setPosition(fittedHJC);

    // The final cup-group transform will be calculated from the cup position and rotation,
    // but we set it here anyway.
    scene.nodes.cupGroup.transform.copy(cupGroupTransform);

    // Transformation from world (CT) space to cup-group space
    const worldToCupGroup = cupGroupTransform.clone().invert();

    // Set cup-mesh transforms
    const localCupTransform = worldToCupGroup.clone().multiply(cupTransform);
    scene.nodes.cup.transform.copy(localCupTransform);
    if (model.case?.operationalSide === 'left') {
        // Rotate left-side cups 90 degrees to align the holes superiorly and posteriorly.
        scene.nodes.cup.transform.multiply(new Matrix4().makeRotationY(-Math.PI / 2));
    }
    scene.nodes.cupCoverage.transform.copy(localCupTransform);
    scene.nodes.cupCollisionSurface.transform.copy(localCupTransform);

    // Set liner transform
    scene.nodes.liner.transform.copy(worldToCupGroup.clone().clone().multiply(linerTransform));

    // Set axis transforms
    scene.nodes.cupNormalAxis.direction
        .copy(model.fittedCup.normal)
        .transformDirection(worldToCupGroup);
    scene.nodes.cupApAxis.direction
        .copy(model.fittedCup.apVector)
        .transformDirection(worldToCupGroup);
    scene.nodes.cupSiAxis.direction
        .copy(model.fittedCup.siVector)
        .transformDirection(worldToCupGroup);

    // Setup coronal cross-section transform
    scene.nodes.cupCoronalCrossSection.transform.copy(
        planeTransform(
            positionalPart(scene.nodes.cup.transform),
            model.fittedCup.apVector.clone().transformDirection(worldToCupGroup),
        ),
    );
}

function _updateCupGroupTransform(model: PlannerStore, scene: PlannerSceneViewModel): StopHandle {
    const logRotation = useDebounceFn(logValidation, 500, { maxWait: 1000 });
    const logPosition = useDebounceFn(logValidation, 500, { maxWait: 1000 });

    return watchEffect(() => {
        if (!model.fittedCup || !model.template || !model.case) {
            return;
        }
        const rotation = _calculateCupGroupRotation(
            model.case.operationalSide,
            model.fittedCup,
            model.template.cupRotation,
            logRotation,
        );
        const position = _calculateCupGroupPosition(
            model.fittedCup,
            model.template.cupOffset,
            logPosition,
        );

        // Set the position of the cupGroup
        scene.nodes.cupGroup.transform.copy(rotation).setPosition(position);
    });
}

function _calculateCupGroupPosition(
    fittedCup: FittedCup,
    offset: AnatomicalOffset,
    log: LogFunction,
): Vector3 {
    const nativeHjc = fittedCup.basis.position.clone();
    const cupPosition = cupWorldOffsetFromApi(fittedCup, offset).add(nativeHjc);
    log(
        [
            'Cup position:',
            `  native hjc: ${formatVector(nativeHjc)}`,
            `  offsets: si: ${formatFloat(offset.si)}  ap: ${formatFloat(offset.ap)}  ml: ${formatFloat(offset.ml)}`,
            '  anatomical basis:',
            `    si: ${formatVector(fittedCup.basis.inferior)}`,
            `    ap: ${formatVector(fittedCup.basis.posterior)}`,
            `    ml: ${formatVector(fittedCup.basis.lateral)}`,
            `  cup-position: ${formatVector(cupPosition)}`,
        ].join('\n'),
    );

    return cupPosition;
}

function _calculateCupGroupRotation(
    side: SideId,
    fittedCup: FittedCup,
    angles: AcetabularAngles<'anatomic'>,
    log: LogFunction,
): Matrix4 {
    let { anteversion, inclination } = angles;
    if (side === 'right') {
        anteversion = -anteversion;
        inclination = -inclination;
    }

    const { posterior, inferior } = fittedCup.basis;
    const anterior = posterior.clone().negate();

    // In the compute code the transformation is implicit in the calculation of the normal, x and y vectors of
    // the 'cup plane', so those variable names are retained here.
    const normal = inferior
        .clone()
        .applyAxisAngle(anterior, inclination)
        .applyAxisAngle(inferior, anteversion)
        .normalize()
        .negate();
    const x = cross(anterior, normal).normalize();
    const y = cross(normal, x).normalize();

    // This is the mapping from a 'plane' to a transform matrix that is applied in this python function:
    // acid.lib.atlas.acetabularcup.acetabular_cup.AcetabularCupAtlas.transform_2_plane
    const rotation = new Matrix4().makeBasis(x.negate(), normal, y);

    assert(isRigidMatrix(rotation), 'Rotation is not a rigid transform');

    log(
        [
            'Cup rotation:',
            `  anteversion: ${formatRadians(anteversion)} (${formatRadiansAsDegrees(anteversion)})`,
            `  inclination: ${formatRadians(inclination)} (${formatRadiansAsDegrees(inclination)})`,
            '  anatomical basis:',
            `    si: ${formatVector(inferior)}`,
            `    ap: ${formatVector(posterior)}`,
            `  cup-normal: ${formatVector(normal)}`,
            `  cup-x: ${formatVector(x)}`,
            `  cup-y: ${formatVector(y)}`,
        ].join('\n'),
    );

    return rotation;
}

function _logCupGroupTransforms(model: PlannerStore, scene: PlannerSceneViewModel): StopHandle {
    return watch(
        () =>
            model.isLoading
                ? null
                : {
                      cupGroup: scene.nodes.cupGroup.transform,
                      cup: scene.nodes.cup.transform,
                      liner: scene.nodes.liner.transform,
                  },
        (transforms) => {
            if (transforms) {
                const { cupGroup, cup, liner } = transforms;
                const cupWorld = cupGroup.clone().multiply(cup);
                const linerWorld = cupGroup.clone().multiply(liner);
                logValidation(
                    joinIndented(2)([
                        `Cup-group transforms:`,
                        'cup-group:',
                        ...indent(2)(formatMatrixBasis(cupGroup)),
                        'cup (world):',
                        ...indent(2)(formatMatrixBasis(cupWorld)),
                        'liner (world):',
                        ...indent(2)(formatMatrixBasis(linerWorld)),
                    ]),
                );
            }
        },
        { deep: true },
    );
}
